import { Marked } from 'marked';

/**
 * `marked`, configured to reproduce the exact HTML our previous markdown engine
 * (showdown, with `{ openLinksInNewWindow, simpleLineBreaks }`) produced, so the
 * swap is a no-op for rendered content. Parity choices:
 *  - `gfm` + `breaks`: a single `\n` becomes `<br>` (was `simpleLineBreaks`).
 *  - `url` / `del` / `table` tokenizers disabled: showdown had autolink,
 *    strikethrough and tables off by default, so bare URLs, `~~text~~` and
 *    `| tables |` must stay literal text.
 *  - link renderer: every link opens in a new tab (was `openLinksInNewWindow`),
 *    with attributes in showdown's order (href, title, rel, target).
 *
 * showdown also emitted `id` attributes on headings; we intentionally drop them
 * — nothing in the app consumes those ids and no markdown uses in-page anchor
 * links. Add a heading renderer back if that ever changes.
 */
const markedInstance = new Marked({ gfm: true, breaks: true });
markedInstance.use({
  tokenizer: {
    url: () => undefined,
    del: () => undefined,
    table: () => undefined,
  },
  renderer: {
    link(token) {
      const title = token.title ? ` title="${token.title}"` : '';
      const text = this.parser.parseInline(token.tokens);
      return `<a href="${token.href}"${title} rel="noopener noreferrer" target="_blank">${text}</a>`;
    },
  },
});

export const markdownToHtml = (markdown: string): string =>
  markedInstance.parse(markdown ?? '', { async: false });

/**
 * Normalizes a string for search by removing diacritics and converting to lowercase.
 * This allows users to search without needing exact accent marks.
 *
 * @example
 * normalizeForSearch('Škoda') // returns 'skoda'
 * normalizeForSearch('Citroën') // returns 'citroen'
 * normalizeForSearch('Björk') // returns 'bjork'
 *
 * @param str - The string to normalize
 * @returns The normalized string in lowercase without diacritics
 */
export const normalizeForSearch = (str: string): string => {
  return str
    .normalize('NFD') // Decompose characters (e.g., 'é' becomes 'e' + combining acute accent)
    .replace(/[\u0300-\u036f]/g, '') // Remove combining diacritical marks
    .toLowerCase();
};

/**
 * Strips markdown formatting from a string, leaving only plain text.
 * Removes links, bold, italic, and other markdown syntax.
 *
 * @example
 * stripMarkdown('[Lorem ipsum](https://wayke.se) **dolor sit amet**')
 * // returns 'Lorem ipsum dolor sit amet'
 *
 * @param markdown - The markdown string to strip
 * @returns The plain text without markdown formatting
 */
export const stripMarkdown = (markdown: string): string => {
  return (
    markdown
      // Remove links but keep link text: [text](url) -> text
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      // Remove bold: **text** or __text__ -> text
      .replace(/(\*\*|__)(.*?)\1/g, '$2')
      // Remove italic: *text* or _text_ -> text
      .replace(/(\*|_)(.*?)\1/g, '$2')
      // Remove inline code: `text` -> text
      .replace(/`([^`]+)`/g, '$1')
      // Remove strikethrough: ~~text~~ -> text
      .replace(/~~(.*?)~~/g, '$1')
      // Remove headers: ## text -> text
      .replace(/^#{1,6}\s+/gm, '')
      // Remove horizontal rules
      .replace(/^(\*{3,}|-{3,}|_{3,})$/gm, '')
      // Remove blockquotes: > text -> text
      .replace(/^>\s+/gm, '')
      // Remove list markers: * text or - text or 1. text -> text
      .replace(/^[\s]*[-*+]\s+/gm, '')
      .replace(/^[\s]*\d+\.\s+/gm, '')
      // Clean up extra whitespace
      .replace(/\s+/g, ' ')
      .trim()
  );
};
